import useServerPagination,{ServerPageStatus} from '../../components/ui/useServerPagination';
import PermissionButton from "../../components/ui/PermissionButton";
import { useEffect, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import InventoryTable from "./InventoryTable";
import AddItemModal from "./AddItemModal";
import AddCategoryModal from "./AddCategoryModal";
import AddDepartmentModal from "./AddDepartmentModal";
import ImportInventoryModal from "./ImportInventoryModal";
import { useInventory } from "../Context/InventoryContext";
import { useAuth } from "../Context/AuthContext";
import EditInventoryModal from "./EditInventoryModal";
import InventoryPriceExcel from './InventoryPriceExcel';

export default function InventoryPage() {
  const { addNewItem, fetchItems:refreshInventoryContext } = useInventory();

  const { user, branchId } = useAuth();

  const [tab, setTab] = useState("raw");

  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [editingItem, setEditingItem] = useState(null);

  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);

  const canFilterByBranch = user?.role === "super_admin";
  const itemsPerPage = 10;

  const fetchCategories = async () => {
    try {
      const response = await fetch(
        "/api/inventory/categories",
        {
          credentials: "include",
        },
      );

      const data = await response.json();

      if (data.status === "success") {
        setCategories((data.data || []).map((category) => category.name));
      }
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetch(
        "/api/inventory/departments",
        {
          credentials: "include",
        },
      );

      const data = await response.json();

      if (data.status === "success") {
        setDepartments(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch departments", error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (!canFilterByBranch) {
      setBranches([]);
      setBranchFilter("");
      return;
    }

    fetch("/api/branches", {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => setBranches(data.data || []))
      .catch(console.error);
  }, [canFilterByBranch]);

  const [pageItems,setPageItems]=useState([]);
  const server=useServerPagination({endpoint:'/inventory/items',onData:setPageItems,search,page:currentPage,pageSize:itemsPerPage,onPageChange:setCurrentPage,filters:{type:tab,department_name:departmentFilter,category:categoryFilter,branch_name:canFilterByBranch?branchFilter:'',stock_status:stockFilter}});

  const totalPages = server.pagination.pages;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedItems = pageItems;

  const resetPage = () => setCurrentPage(1);

  const refreshAfterImport = async () => {
    if (typeof fetchItems === "function") {
      await fetchItems();
    } else {
      window.location.reload();
    }
  };

  const fetchItems=async()=>{await refreshInventoryContext?.();server.reload();};

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
          <div>
            <h1 className="text-2xl font-serif">Inventory</h1>
            <p className="text-sm text-gray-400">
              Raw Material & Finished Goods Management
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <InventoryPriceExcel onSaved={fetchItems}/>
            <PermissionButton permissionModule="inventory"
              onClick={() => setShowAddDepartment(true)}
              className="rounded-xl border border-white/20 px-4 py-2"
            >
              + Add Department
            </PermissionButton>

            <PermissionButton permissionModule="inventory"
              onClick={() => setShowAddCategory(true)}
              className="rounded-xl border border-white/20 px-4 py-2"
            >
              + Add Category
            </PermissionButton>

            <PermissionButton permissionModule="inventory"
              onClick={() => setShowImport(true)}
              className="rounded-xl border border-emerald-400/50 px-4 py-2 text-emerald-400 transition hover:bg-emerald-400/10"
            >
              Import Excel
            </PermissionButton>

            <PermissionButton permissionModule="inventory"
              onClick={() => setShowAddItem(true)}
              className="rounded-xl bg-amber-400 px-5 py-2 font-medium text-black"
            >
              + Add Item
            </PermissionButton>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex w-fit gap-2 rounded-xl bg-black/40 p-1">
            <PermissionButton permissionModule="inventory"
              onClick={() => {
                setTab("raw");
                resetPage();
              }}
              className={`rounded-lg px-5 py-2 text-sm transition ${
                tab === "raw"
                  ? "bg-amber-400 font-medium text-black"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              Raw Material
            </PermissionButton>

            <PermissionButton permissionModule="inventory"
              onClick={() => {
                setTab("finished");
                resetPage();
              }}
              className={`rounded-lg px-5 py-2 text-sm transition ${
                tab === "finished"
                  ? "bg-amber-400 font-medium text-black"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              Finished Goods
            </PermissionButton>
          </div>

          <p className="text-xs text-gray-500">
            Physical stock selection:{" "}
            <span className="font-medium text-amber-400">
              Per item — FIFO / LIFO / FEFO (see stock lots)
            </span>
          </p>
        </div>

        <div
          className={`grid grid-cols-1 gap-3 md:grid-cols-2 ${
            canFilterByBranch ? "xl:grid-cols-5" : "xl:grid-cols-4"
          }`}
        >
          <input
            placeholder="Search inventory..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPage();
            }}
            className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
          />

          <select
            value={departmentFilter}
            onChange={(e) => {
              setDepartmentFilter(e.target.value);
              resetPage();
            }}
            className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
          >
            <option value="">All Departments</option>

            {departments.map((department) => (
              <option key={department.id} value={department.name}>
                {department.name}
              </option>
            ))}
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              resetPage();
            }}
            className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
          >
            <option value="">All Categories</option>

            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          {canFilterByBranch && (
            <select
              value={branchFilter}
              onChange={(e) => {
                setBranchFilter(e.target.value);
                resetPage();
              }}
              className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
            >
              <option value="">All Branches</option>

              {branches.map((branch) => (
                <option key={branch.id} value={branch.name}>
                  {branch.name}
                </option>
              ))}
            </select>
          )}

          <select
            value={stockFilter}
            onChange={(e) => {
              setStockFilter(e.target.value);
              resetPage();
            }}
            className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
          >
            <option value="all">All Stock Status</option>
            <option value="healthy">Healthy Stock</option>
            <option value="low">Low Stock</option>
          </select>
        </div>

        <ServerPageStatus pagination={server.pagination}/>
        <InventoryTable
          items={paginatedItems}
          onUpdated={fetchItems}
          onEdit={(item) => setEditingItem(item)}
        />

        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <p className="text-sm text-gray-400">
            Showing {paginatedItems.length} of {server.pagination.total} items
          </p>

          <div className="flex items-center gap-3">
            <PermissionButton permissionModule="inventory"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((page) => page - 1)}
              className="rounded-lg border border-white/20 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </PermissionButton>

            <span className="text-sm text-gray-400">
              Page {currentPage} of {totalPages}
            </span>

            <PermissionButton permissionModule="inventory"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((page) => page + 1)}
              className="rounded-lg border border-white/20 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </PermissionButton>
          </div>
        </div>

        {showAddItem && (
          <AddItemModal
            onClose={() => setShowAddItem(false)}
            categories={categories}
            departments={departments}
            user={{
              ...user,
              branch_id: branchId,
            }}
            onSave={addNewItem}
          />
        )}

        {showAddCategory && (
          <AddCategoryModal
            onClose={() => setShowAddCategory(false)}
            onSave={(category) => {
              setCategories((previous) => [...previous, category]);
              setShowAddCategory(false);
            }}
          />
        )}

        {showAddDepartment && (
          <AddDepartmentModal
            onClose={() => setShowAddDepartment(false)}
            onSave={(department) => {
              setDepartments((previous) =>
                [...previous, department].sort((a, b) =>
                  a.name.localeCompare(b.name),
                ),
              );
            }}
          />
        )}

        {showImport && (
          <ImportInventoryModal
            onClose={() => setShowImport(false)}
            onImported={refreshAfterImport}
          />
        )}

        {editingItem && (
          <EditInventoryModal
            item={editingItem}
            categories={categories}
            departments={departments}
            onClose={() => setEditingItem(null)}
            onSaved={async () => {
              await fetchItems();
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
